import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Typography, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormGroup, FormControlLabel, Checkbox, Chip, Grid, Divider, Alert,
} from "@mui/material";
import api from "../services/api";

/* Platform-owner only: create/manage subscriber companies + their admin login. */
export default function CompaniesAdmin() {
  const [companies, setCompanies] = useState([]);
  const [allModules, setAllModules] = useState([]); // [{key,label,group}]
  const [error, setError] = useState("");

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);     // company being edited (or null = new)
  const [form, setForm] = useState({});
  const [licensed, setLicensed] = useState(new Set()); // module keys this company gets

  const [adminOpen, setAdminOpen] = useState(false);
  const [adminFor, setAdminFor] = useState(null);
  const [adminForm, setAdminForm] = useState({ username: "", password: "" });

  const load = useCallback(() => {
    api.get("/companies")
      .then((r) => setCompanies(Array.isArray(r.data) ? r.data : []))
      .catch((e) => setError(e.response?.data?.error || "Failed to load companies"));
  }, []);

  useEffect(() => {
    load();
    api.get("/companies/modules")
      .then((r) => setAllModules(Array.isArray(r.data?.modules) ? r.data.modules : []))
      .catch(() => {});
  }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm({ Code: "", Name: "", Address: "", Contact: "", PurchaseContact: "",
      WireBeneficiary: "", WireBankName: "", WireBankAddress: "", WireCountry: "",
      WireAccountNo: "", WireSwift: "", WireRouting: "", ExpiresOn: "" });
    setLicensed(new Set(allModules.map((m) => m.key))); // new company: all on by default
    setEditOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      Code: c.Code, Name: c.Name, Address: c.Address || "", Contact: c.Contact || "",
      PurchaseContact: c.PurchaseContact || "", WireBeneficiary: c.WireBeneficiary || "",
      WireBankName: c.WireBankName || "", WireBankAddress: c.WireBankAddress || "",
      WireCountry: c.WireCountry || "", WireAccountNo: c.WireAccountNo || "",
      WireSwift: c.WireSwift || "", WireRouting: c.WireRouting || "",
      ExpiresOn: c.ExpiresOn ? String(c.ExpiresOn).slice(0, 10) : "",
    });
    const disabled = new Set(String(c.DisabledModules || "").split(",").map((s) => s.trim()).filter(Boolean));
    setLicensed(new Set(allModules.map((m) => m.key).filter((k) => !disabled.has(k))));
    setEditOpen(true);
  };

  const toggleModule = (key) => {
    setLicensed((prev) => {
      const n = new Set(prev);
      n.has(key) ? n.delete(key) : n.add(key);
      return n;
    });
  };

  const saveCompany = async () => {
    setError("");
    // licensed -> disabledModules (the complement)
    const DisabledModules = allModules.map((m) => m.key).filter((k) => !licensed.has(k));
    const payload = { ...form, DisabledModules, ExpiresOn: form.ExpiresOn || null };
    try {
      if (editing) await api.put(`/companies/${editing.Company_ID}`, payload);
      else await api.post("/companies", payload);
      setEditOpen(false);
      load();
    } catch (e) {
      setError(e.response?.data?.error || "Save failed");
    }
  };

  const toggleActive = async (c) => {
    try {
      await api.post(`/companies/${c.Company_ID}/active`, { isActive: !c.IsActive });
      load();
    } catch (e) {
      setError(e.response?.data?.error || "Status change failed");
    }
  };

  const openAdmin = (c) => { setAdminFor(c); setAdminForm({ username: "", password: "" }); setAdminOpen(true); };
  const saveAdmin = async () => {
    setError("");
    try {
      await api.post(`/companies/${adminFor.Company_ID}/admin`, adminForm);
      setAdminOpen(false);
      load();
    } catch (e) {
      setError(e.response?.data?.error || "Failed to create admin");
    }
  };

  const groups = [...new Set(allModules.map((m) => m.group))];

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>Companies (Subscribers)</Typography>
        <Button variant="contained" onClick={openNew}>+ Add Company</Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell><TableCell>Code</TableCell><TableCell>Name</TableCell>
              <TableCell align="right">Users</TableCell><TableCell>Modules off</TableCell>
              <TableCell>Status</TableCell><TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {companies.map((c) => {
              const off = String(c.DisabledModules || "").split(",").map((s) => s.trim()).filter(Boolean);
              return (
                <TableRow key={c.Company_ID} hover>
                  <TableCell>{c.Company_ID}</TableCell>
                  <TableCell>{c.Code}</TableCell>
                  <TableCell>{c.Name}</TableCell>
                  <TableCell align="right">{c.UserCount}</TableCell>
                  <TableCell>{off.length ? off.join(", ") : <span style={{ color: "#888" }}>none</span>}</TableCell>
                  <TableCell>
                    <Chip size="small" label={c.IsActive ? "Active" : "Suspended"} color={c.IsActive ? "success" : "default"} />
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => openEdit(c)}>Edit</Button>
                    <Button size="small" onClick={() => openAdmin(c)}>Add Admin</Button>
                    <Button size="small" color={c.IsActive ? "warning" : "success"} onClick={() => toggleActive(c)}>
                      {c.IsActive ? "Suspend" : "Activate"}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
            {!companies.length && (
              <TableRow><TableCell colSpan={7} align="center" sx={{ color: "text.secondary" }}>No companies yet</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create / Edit company */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? `Edit ${editing.Name}` : "Add Company"}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField label="Code" fullWidth value={form.Code || ""} disabled={!!editing}
                onChange={(e) => setForm({ ...form, Code: e.target.value })} helperText="Short unique code" />
            </Grid>
            <Grid size={{ xs: 12, sm: 5 }}>
              <TextField label="Company Name" fullWidth value={form.Name || ""}
                onChange={(e) => setForm({ ...form, Name: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label="Subscription expires" type="date" fullWidth InputLabelProps={{ shrink: true }}
                value={form.ExpiresOn || ""} onChange={(e) => setForm({ ...form, ExpiresOn: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
              <TextField label="Address (letterhead)" fullWidth value={form.Address || ""}
                onChange={(e) => setForm({ ...form, Address: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label="Contact line" fullWidth value={form.Contact || ""}
                onChange={(e) => setForm({ ...form, Contact: e.target.value })} />
            </Grid>

            <Grid size={12}><Divider><Chip label="Wire / Bank (Sale Invoice)" size="small" /></Divider></Grid>
            <Grid size={{ xs: 12, sm: 6 }}><TextField label="Beneficiary" fullWidth value={form.WireBeneficiary || ""} onChange={(e) => setForm({ ...form, WireBeneficiary: e.target.value })} /></Grid>
            <Grid size={{ xs: 12, sm: 6 }}><TextField label="Bank name" fullWidth value={form.WireBankName || ""} onChange={(e) => setForm({ ...form, WireBankName: e.target.value })} /></Grid>
            <Grid size={{ xs: 12, sm: 6 }}><TextField label="Bank address" fullWidth value={form.WireBankAddress || ""} onChange={(e) => setForm({ ...form, WireBankAddress: e.target.value })} /></Grid>
            <Grid size={{ xs: 12, sm: 3 }}><TextField label="Account #" fullWidth value={form.WireAccountNo || ""} onChange={(e) => setForm({ ...form, WireAccountNo: e.target.value })} /></Grid>
            <Grid size={{ xs: 12, sm: 3 }}><TextField label="SWIFT" fullWidth value={form.WireSwift || ""} onChange={(e) => setForm({ ...form, WireSwift: e.target.value })} /></Grid>
            <Grid size={{ xs: 12, sm: 3 }}><TextField label="Routing #" fullWidth value={form.WireRouting || ""} onChange={(e) => setForm({ ...form, WireRouting: e.target.value })} /></Grid>
            <Grid size={{ xs: 12, sm: 3 }}><TextField label="Country" fullWidth value={form.WireCountry || ""} onChange={(e) => setForm({ ...form, WireCountry: e.target.value })} /></Grid>

            <Grid size={12}><Divider><Chip label="Licensed modules (ticked = available to this company)" size="small" /></Divider></Grid>
            {groups.map((g) => (
              <Grid size={{ xs: 12, sm: 4 }} key={g}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>{g}</Typography>
                <FormGroup>
                  {allModules.filter((m) => m.group === g).map((m) => (
                    <FormControlLabel key={m.key}
                      control={<Checkbox size="small" checked={licensed.has(m.key)} onChange={() => toggleModule(m.key)} />}
                      label={<span style={{ fontSize: 13 }}>{m.label}</span>} />
                  ))}
                </FormGroup>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveCompany}>{editing ? "Save" : "Create"}</Button>
        </DialogActions>
      </Dialog>

      {/* Create admin */}
      <Dialog open={adminOpen} onClose={() => setAdminOpen(false)}>
        <DialogTitle>Add admin for {adminFor?.Name}</DialogTitle>
        <DialogContent dividers sx={{ minWidth: 360 }}>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Username must be unique across all companies (it's the single login).
          </Typography>
          <TextField label="Username" fullWidth sx={{ mt: 2 }} value={adminForm.username}
            onChange={(e) => setAdminForm({ ...adminForm, username: e.target.value })} />
          <TextField label="Password" type="password" fullWidth sx={{ mt: 2 }} value={adminForm.password}
            onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdminOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveAdmin}>Create Admin</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
